import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Trophy, ArrowLeft, Eye, Heart, Star, User, 
  Calendar, Sparkles, Award, Crown, Pencil
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const AWARD_ICONS = {
  "Best Character Development": Crown,
  "Best Hook": Sparkles,
  "Best Conceptualization": Star,
};

const ChallengeResultsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingAwards, setEditingAwards] = useState(false);
  const [awardSelections, setAwardSelections] = useState({});
  const [savingAwards, setSavingAwards] = useState(false);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const res = await api.get(`/challenges/${id}/results`);
        setData(res.data);
      } catch (error) {
        
        navigate('/challenges');
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [id, api, navigate]);

  const handleSaveAwards = async () => {
    const awards = Object.entries(awardSelections)
      .filter(([_, entryId]) => entryId)
      .map(([award, entry_id]) => ({ entry_id, award }));
    if (awards.length === 0) { alert('Please select at least one winner.'); return; }
    setSavingAwards(true);
    try {
      await api.post(`/challenges/${id}/announce-results`, {
        awards,
        announcement_message: data.announcement_message || ''
      });
      const res = await api.get(`/challenges/${id}/results`);
      setData(res.data);
      setEditingAwards(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save awards');
    } finally {
      setSavingAwards(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Skeleton className="h-8 w-40 mb-8" />
        <Skeleton className="h-16 w-3/4 mb-6" />
        <div className="space-y-6">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-48 w-full rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { challenge, results, announced, announcement_message } = data;

  // Separate winners (have awards) from other entries
  const winners = results.filter(r => r.awards && r.awards.length > 0);
  const others = results.filter(r => !r.awards || r.awards.length === 0);

  // Group winners by award for a cleaner display
  const awardMap = {};
  winners.forEach(w => {
    w.awards.forEach(award => {
      if (!awardMap[award]) awardMap[award] = [];
      awardMap[award].push(w);
    });
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="challenge-results-page">
      {/* Back */}
      <Link 
        to={`/challenges/${id}`} 
        className="inline-flex items-center gap-2 text-text-muted hover:text-text-primary mb-8 transition-colors"
        data-testid="back-to-challenge"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Challenge
      </Link>

      {/* Header */}
      <header className="text-center mb-12">
        <div className="inline-flex items-center gap-2 mb-4">
          <Trophy className="w-6 h-6 text-brand-accent" />
          <span className="text-sm font-semibold text-brand-accent uppercase tracking-wider">
            Challenge Results
          </span>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-text-primary mb-4">
          {challenge.title}
        </h1>
        {announced && announcement_message && (
          <p className="text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed">
            {announcement_message}
          </p>
        )}
        <div className="flex items-center justify-center gap-6 mt-6 text-sm text-text-muted">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {new Date(challenge.end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </span>
          <span>{results.length} entries</span>
        </div>
      </header>

      {/* Admin: Edit Awards */}
      {user?.role === 'admin' && announced && (
        <div className="mb-10" data-testid="edit-awards-section">
          {!editingAwards ? (
            <div className="text-center">
              <Button onClick={() => {
                const current = {};
                results.forEach(r => { (r.awards || []).forEach(a => { current[a] = r.entry_id; }); });
                setAwardSelections(current);
                setEditingAwards(true);
              }} className="gap-2" variant="outline" data-testid="edit-awards-btn">
                <Pencil className="w-4 h-4" />
                Edit Awards
              </Button>
            </div>
          ) : (
            <Card className="border-brand-accent/20 bg-brand-accent/5">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-serif text-lg font-semibold text-text-primary">Assign Awards</h3>
                {challenge.award_categories?.map((category) => (
                  <div key={category}>
                    <label className="text-sm font-medium text-text-primary mb-1 block">{category}</label>
                    <Select value={awardSelections[category] || ''} onValueChange={(val) => setAwardSelections(prev => ({ ...prev, [category]: val }))}>
                      <SelectTrigger data-testid={`edit-award-${category}`}>
                        <SelectValue placeholder="Select winner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {results.map((r) => r.story && (
                          <SelectItem key={r.entry_id} value={r.entry_id}>
                            {r.story.title} — {r.story.author_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                <div className="flex gap-3 pt-2">
                  <Button onClick={handleSaveAwards} disabled={savingAwards} className="btn-accent" data-testid="save-awards-btn">
                    {savingAwards ? 'Saving...' : 'Save Awards'}
                  </Button>
                  <Button onClick={() => setEditingAwards(false)} variant="outline">Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Award Winners Section */}
      {Object.keys(awardMap).length > 0 && (
        <section className="mb-16" data-testid="winners-section">
          <div className="space-y-10">
            {Object.entries(awardMap).map(([awardName, awardWinners]) => {
              const IconComp = AWARD_ICONS[awardName] || Award;
              return (
                <div key={awardName}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-brand-accent/15 flex items-center justify-center">
                      <IconComp className="w-5 h-5 text-brand-accent" />
                    </div>
                    <h2 className="font-serif text-2xl font-semibold text-text-primary">
                      {awardName}
                    </h2>
                  </div>
                  <div className="space-y-4">
                    {awardWinners.map((winner) => (
                      <WinnerCard key={`${awardName}-${winner.entry_id}`} result={winner} awardName={awardName} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Not Announced Yet */}
      {!announced && winners.length === 0 && (
        <div className="text-center py-16 bg-white rounded-2xl border border-brand-primary/10 mb-12" data-testid="not-announced-message">
          <Trophy className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <h2 className="font-serif text-2xl font-semibold text-text-primary mb-2">
            Results Not Yet Announced
          </h2>
          <p className="text-text-secondary">
            The judges are still deliberating. Check back soon!
          </p>
        </div>
      )}

      {/* All Entries with Scores */}
      {results.length > 0 && (
        <section data-testid="all-entries-section">
          <h2 className="font-serif text-2xl font-semibold text-text-primary mb-6">
            All Entries & Scores
          </h2>
          <div className="space-y-4">
            {results.map((result) => (
              <EntryScoreCard key={result.entry_id} result={result} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

const WinnerCard = ({ result, awardName }) => {
  const story = result.story;
  if (!story) return null;

  return (
    <Card className="bg-gradient-to-r from-brand-accent/5 to-white border-brand-accent/20 overflow-hidden" data-testid={`winner-card-${result.entry_id}`}>
      <CardContent className="p-6">
        <div className="flex items-start gap-5">
          {story.cover_image && (
            <img 
              src={story.cover_image} 
              alt={story.title}
              className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <Link 
              to={`/story/${story.id}`}
              className="font-serif text-xl font-semibold text-text-primary hover:text-brand-accent transition-colors"
            >
              {story.title}
            </Link>
            <p className="text-sm text-text-muted mt-1">by {story.author_name}</p>
            {story.summary && (
              <p className="text-text-secondary text-sm mt-2 line-clamp-2">{story.summary}</p>
            )}
            <div className="flex items-center gap-4 mt-3 text-sm text-text-muted">
              <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> {story.view_count}</span>
              <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> {story.like_count}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EntryScoreCard = ({ result }) => {
  const story = result.story;
  if (!story) return null;

  const hasScores = Object.keys(result.category_scores || {}).length > 0;

  return (
    <Card className="bg-white border-brand-primary/10" data-testid={`entry-score-${result.entry_id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Link 
                to={`/story/${story.id}`}
                className="font-serif text-lg font-semibold text-text-primary hover:text-brand-accent transition-colors"
              >
                {story.title}
              </Link>
              {result.awards?.length > 0 && (
                <div className="flex gap-1">
                  {result.awards.map((award) => (
                    <Badge key={`award-${award}`} className="bg-brand-accent/15 text-brand-accent text-xs border-0">
                      <Trophy className="w-3 h-3 mr-1" />{award}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <p className="text-sm text-text-muted">by {story.author_name}</p>
          </div>
        </div>

        {/* Scores by category */}
        {hasScores && (
          <div className="space-y-3 mt-4">
            {Object.entries(result.category_scores).map(([category, scores]) => {
              const avgScore = (scores.reduce((acc, s) => acc + s.score, 0) / scores.length).toFixed(1);
              return (
                <div key={category} className="bg-surface-alt rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-text-primary">{category}</span>
                    <span className="text-sm font-semibold text-brand-accent">{avgScore}/10</span>
                  </div>
                  <div className="w-full bg-brand-primary/10 rounded-full h-1.5">
                    <div 
                      className="bg-brand-accent h-1.5 rounded-full transition-all"
                      style={{ width: `${(avgScore / 10) * 100}%` }}
                    />
                  </div>
                  {/* Judge feedback */}
                  <div className="mt-2 space-y-1">
                    {scores.map((score) => (
                      <p key={`score-${score.judge_name}-${category}`} className="text-xs text-text-muted">
                        <span className="font-medium">{score.judge_name}:</span> {score.feedback}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!hasScores && (
          <p className="text-sm text-text-muted mt-2 italic">No scores submitted yet</p>
        )}
      </CardContent>
    </Card>
  );
};

export default ChallengeResultsPage;
